import type { ReactNode } from "react";
import { Callout } from "./ui";

export type ReadOnlyFailureReason =
  | "canonical-write-api-unmounted"
  | "demo-not-served-to-real"
  | "oracle-not-independent";

type ReadOnlyFailureResult = {
  ok: false;
  message: string;
  reason: ReadOnlyFailureReason;
};

export type ReadOnlyFailureNotice = {
  message: string;
  reason: ReadOnlyFailureReason;
};

export type ActionNotice = string | ReadOnlyFailureNotice;

export function readOnlyFailureNotice(result: ReadOnlyFailureResult): ReadOnlyFailureNotice {
  return {
    message: result.message || result.reason,
    reason: result.reason,
  };
}

export function readOnlyFailureNoticeFromError(err: unknown): ReadOnlyFailureNotice | null {
  if (!err || typeof err !== "object") return null;
  const raw = "reason" in err ? err.reason : "error" in err ? err.error : null;
  if (
    raw !== "canonical-write-api-unmounted" &&
    raw !== "demo-not-served-to-real" &&
    raw !== "oracle-not-independent"
  ) {
    return null;
  }
  const message = "message" in err && typeof err.message === "string" ? err.message : raw;
  return { message, reason: raw };
}

function reasonAttrs(reason: ReadOnlyFailureReason): Record<string, string> {
  switch (reason) {
    case "canonical-write-api-unmounted":
      return { "data-canonical-write-api-unmounted-error": "true" };
    case "demo-not-served-to-real":
      return { "data-demo-not-served-to-real-error": "true" };
    case "oracle-not-independent":
      return { "data-oracle-not-independent-error": "true" };
  }
}

export function ReadOnlyFailureCallout({
  notice,
  title = "Action result",
  actionId,
}: {
  notice: ActionNotice;
  title?: ReactNode;
  actionId: string;
}) {
  if (typeof notice === "string") {
    return (
      <Callout tone="neutral" title={title} actionId={actionId}>
        {notice}
      </Callout>
    );
  }
  return (
    <Callout
      tone="fail"
      title={title}
      actionId={actionId}
      data-read-only-error-reason={notice.reason}
      {...reasonAttrs(notice.reason)}
    >
      {notice.message}
    </Callout>
  );
}
